import { useState, useEffect } from "react";
import styles from "../HealthChat.module.css";
import { useAuth } from '../../../hooks/useAuth';
import { isMobileVerified, storePatientSession } from '../../../utils/storage/authStorage';
import { quickRegister, sendQuickOtp, verifyOtp } from '../../../api/auth.api';
import { useNavigate } from "react-router-dom";
// import StepperProgress from "../Reusablesteps/Stepperprogress";

/* ─── SVG icons ─────────────────────────────────────────────────────────── */
const PersonIcon = ({ size = 52 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const UserIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="#94a3b8"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const PhoneIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="#94a3b8"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 11.69 19a19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.6 2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

/* ─── Copy ───────────────────────────────────────────────────────────────── */
const COPY = {
  en: {
    title: "Patient Details",
    subtitle: "Please provide your information to book the appointment",
    nameLabel: "Full Name",
    namePlaceholder: "Enter your full name",
    phoneLabel: "Phone Number",
    phonePlaceholder: "Enter your phone number",
    backBtn: "Back",
    continueBtn: "Continue",
    sendOtp: "Send OTP",
    verifyOtp: "Verify & Continue",
    otpLabel: "Enter OTP",
    otpSent: "OTP sent to +91 ",
    resend: "Resend OTP",
  },
  hi: {
    title: "रोगी विवरण",
    subtitle: "अपॉइंटमेंट बुक करने के लिए अपनी जानकारी प्रदान करें",
    nameLabel: "पूरा नाम",
    namePlaceholder: "अपना पूरा नाम दर्ज करें",
    phoneLabel: "फ़ोन नंबर",
    phonePlaceholder: "अपना फ़ोन नंबर दर्ज करें",
    backBtn: "वापस",
    continueBtn: "जारी रखें",
    sendOtp: "OTP भेजें",
    verifyOtp: "सत्यापित करें और जारी रखें",
    otpLabel: "OTP दर्ज करें",
    otpSent: "OTP भेजा गया +91 ",
    resend: "OTP पुनः भेजें",
  },
  te: {
    title: "రోగి వివరాలు",
    subtitle: "అపాయింట్మెంట్ బుక్ చేయడానికి మీ సమాచారం అందించండి",
    nameLabel: "పూర్తి పేరు",
    namePlaceholder: "మీ పూర్తి పేరు నమోదు చేయండి",
    phoneLabel: "ఫోన్ నంబర్",
    phonePlaceholder: "మీ ఫోన్ నంబర్ నమోదు చేయండి",
    backBtn: "వెనుకకు",
    continueBtn: "కొనసాగించు",
    sendOtp: "OTP పంపండి",
    verifyOtp: "ధృవీకరించి కొనసాగించు",
    otpLabel: "OTP నమోదు చేయండి",
    otpSent: "OTP పంపబడింది +91 ",
    resend: "OTP మళ్ళీ పంపండి",
  },
};

export default function PatientDetailsScreen({
  lang = "en",
  patientState,
  onPatientStateChange,
  onContinue,
  onBack,
}) {
  const c = COPY[lang] || COPY.en;
  const navigate=useNavigate();
  const { user } = useAuth();
  const[showLogin,setShowLogin]=useState(false);

  // Destructure from shared state
  const {
    name = '',
    mobile = '',
    otp = ['', '', '', '', '', ''],
    otpSent = false,
    otpVerified = false,
    errors = {},
  } = patientState;

  const set = (d) => onPatientStateChange(d);

  const isVerified = isMobileVerified(mobile || user?.mobile || '');

  // Pre-fill from auth session only once (when fields are empty)
  useEffect(() => {
    if (!user) return;
    if (!name && user.fullName) set({ name: user.fullName });
    if (!mobile && user.mobile) set({ mobile: user.mobile });
  }, [user]);

  const [loading, setLoading] = useState(false);

  const canProceed = name.trim() && mobile.trim();

  /* ─── Validation ─────────────────────────────────────────────────────── */
  const validate = () => {
    const newErrors = { ...errors };
    let valid = true;

    if (!name.trim()) {
      newErrors.name = "Name is required";
      valid = false;
    } else {
      newErrors.name = "";
    }

    if (!mobile.trim()) {
      newErrors.mobile = "Phone is required";
      valid = false;
    } else if (!/^\d{10}$/.test(mobile)) {
      newErrors.mobile = "Phone must be 10 digits";
      valid = false;
    } else {
      newErrors.mobile = "";
    }

    set({ errors: newErrors });
    return valid;
  };

  /* ─── Send OTP ───────────────────────────────────────────────────────── */
  const handleSendOtp = async () => {
    if (!validate()) return;

    // If already verified, skip OTP and continue directly
    if (isVerified) {
      onContinue({ name, mobile });
      return;
    }

    setLoading(true);
    try {
      await sendQuickOtp({
        mobileNumber: mobile,
      });
      set({ otpSent: true, errors: { ...errors, otp: "" } });
    } catch (err) {
       if (err?.message?.toLowerCase().includes('already registered') ||
          err?.message?.toLowerCase().includes('please login')) {
          setShowLogin(true);
        }
      set({ errors: { ...errors, mobile: err?.message || "Failed to send OTP. Try again." } });

    } finally {
      setLoading(false);
    }
  };

  /* ─── Verify OTP ─────────────────────────────────────────────────────── */
  const handleVerifyOtp = async () => {
    if (otp.join("").length < 6) {
      set({ errors: { ...errors, otp: "Enter all 6 digits" } });
      return;
    }

    setLoading(true);
    try {
      await verifyOtp({
        fullName: name.trim(),
        mobileNumber: mobile,
        otp: otp.join(""),
      });

      const registerResponse = await quickRegister({
        fullName: name.trim(),
        mobileNumber: mobile,
        otp: otp.join(""),
        orgName: "FemCare",
      });

      storePatientSession({
        ...registerResponse.data,
        fullName: name.trim(),
        mobile: mobile,
        isVerified: true,
      });

      set({ otpVerified: true, errors: { ...errors, otp: "" } });
      onContinue({ name, mobile });
    } catch (err) {
        
      
      set({ errors: { ...errors, otp: err?.message || "Invalid OTP. Please try again." } });
    } finally {
      setLoading(false);
    }
  };

  /* ─── OTP input handler ──────────────────────────────────────────────── */
  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    set({ otp: newOtp });

    // Auto-focus next
    if (value && index < 5) {
      const next = document.getElementById(`otp-input-${index + 1}`);
      if (next) next.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prev = document.getElementById(`otp-input-${index - 1}`);
      if (prev) prev.focus();
    }
  };

  /* ─── Button label logic ─────────────────────────────────────────────── */
  const primaryBtnLabel = isVerified
    ? c.continueBtn
    : otpSent
    ? c.verifyOtp
    : c.sendOtp;

  const handlePrimaryAction = isVerified
    ? () => { if (validate()) onContinue({ name, mobile }); }
    : otpSent
    ? handleVerifyOtp
    : handleSendOtp;

  return (
    <div className={styles.bookingFormScreen}>
      <button className={styles.selectDoctorBack} onClick={onBack}>
        ← Back
      </button>

      {/* Icon */}
      <div className={styles.bookingIconCircle}>
        <PersonIcon size={30} />
      </div>
      <div className={styles.bookingFormTitle}>{c.title}</div>
      <div className={styles.bookingFormSub}>{c.subtitle}</div>

      {/* Full Name */}
      <div className={styles.bookingFieldGroup}>
        <label className={styles.bookingFieldLabel}>{c.nameLabel}</label>
        <div className={styles.bookingInputWrap}>
          <span className={styles.bookingInputIcon}>
            <UserIcon />
          </span>
          <input
            className={styles.bookingInput}
            placeholder={c.namePlaceholder}
            value={name}
            onChange={(e) => set({ name: e.target.value })}
          />
        </div>
        {errors.name && <p className={styles.errorText}>{errors.name}</p>}
      </div>

      {/* Phone */}
      <div className={styles.bookingFieldGroup}>
        <label className={styles.bookingFieldLabel}>{c.phoneLabel}</label>
        <div className={styles.bookingInputWrap}>
          <span className={styles.bookingInputIcon}>
            <PhoneIcon />
          </span>
          <span
            className={`${styles.bookingInputDigit}`}
            style={{ color: "#374151" }}
          >
            +91
          </span>
          <input
            className="px-2 py-3 text-sm outline-none bg-white"
            placeholder={c.phonePlaceholder}
            value={mobile}
            type="tel"
            maxLength={10}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              set({ mobile: value, otpSent: false, otpVerified: false, otp: ['', '', '', '', '', ''] });
            }}
          />
        </div>
        {/* {errors.mobile && <p className={styles.errorText}>{errors.mobile}</p>} */}
        {errors.mobile && (
                  <div className=" flex justify-between items-center mt-3">
                  <p className="text-[10px] text-red-500 mt-1">{errors.mobile}</p>
                     {showLogin && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <button
                            type="button"
                            className="ava-text-caption   hover:underline bg-transparent border-none p-0 cursor-pointer animate-bounce"
                            onClick={() => {
                              // updateIdentifyState({ patientType: 'existing', showLogin: false });
                              // updateErrors({});
                              navigate('/sign-in')
                            }}
                            style={{ color: "var(--primary-color)" }}
                          >
                            Login instead
                          </button>
                        </div>
                      )}
                  </div>
                )}
      </div>

      {/* OTP Section — shown only when not pre-verified and OTP has been sent */}
      {!isVerified && otpSent && (
        <div className={styles.bookingFieldGroup}>
          <label className={styles.bookingFieldLabel}>{c.otpLabel}</label>
          <p style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>
            {c.otpSent}{mobile}
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            {otp.map((digit, i) => (
              <input
                key={i}
                id={`otp-input-${i}`}
                type="tel"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(i, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(i, e)}
                style={{
                  width: 40,
                  height: 44,
                  textAlign: "center",
                  fontSize: 18,
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  outline: "none",
                  background: "#fff",
                }}
              />
            ))}
          </div>
          {errors.otp && <p className={styles.errorText}>{errors.otp}</p>}
          <button
            style={{
              marginTop: 8,
              fontSize: 13,
              color: "#7c3aed",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
            onClick={handleSendOtp}
            disabled={loading}
          >
            {c.resend}
          </button>
        </div>
      )}

      <div className={styles.bookingActions}>
        <button
          className={styles.bookingContinueBtn}
          disabled={!canProceed || loading}
          onClick={handlePrimaryAction}
        >
          {loading ? "Please wait…" : primaryBtnLabel}
        </button>
      </div>
    </div>
  );
}

// import { useState } from "react";
// import styles from "../HealthChat.module.css";

// /* ─── SVG icons ─────────────────────────────────────────────────────────── */
// const PersonIcon = ({ size = 52 }) => (
//   <svg
//     width={size}
//     height={size}
//     viewBox="0 0 24 24"
//     fill="none"
//     stroke="#fff"
//     strokeWidth="1.8"
//     strokeLinecap="round"
//     strokeLinejoin="round"
//   >
//     <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
//     <circle cx="12" cy="7" r="4" />
//   </svg>
// );

// const UserIcon = ({ size = 16 }) => (
//   <svg
//     width={size}
//     height={size}
//     viewBox="0 0 24 24"
//     fill="none"
//     stroke="#94a3b8"
//     strokeWidth="1.8"
//     strokeLinecap="round"
//     strokeLinejoin="round"
//   >
//     <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
//     <circle cx="12" cy="7" r="4" />
//   </svg>
// );

// const MailIcon = ({ size = 16 }) => (
//   <svg
//     width={size}
//     height={size}
//     viewBox="0 0 24 24"
//     fill="none"
//     stroke="#94a3b8"
//     strokeWidth="1.8"
//     strokeLinecap="round"
//     strokeLinejoin="round"
//   >
//     <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
//     <polyline points="22,6 12,13 2,6" />
//   </svg>
// );

// const PhoneIcon = ({ size = 16 }) => (
//   <svg
//     width={size}
//     height={size}
//     viewBox="0 0 24 24"
//     fill="none"
//     stroke="#94a3b8"
//     strokeWidth="1.8"
//     strokeLinecap="round"
//     strokeLinejoin="round"
//   >
//     <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 11.69 19a19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.6 2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
//   </svg>
// );

// /* ─── Copy ───────────────────────────────────────────────────────────────── */
// const COPY = {
//   en: {
//     title: "Patient Details",
//     subtitle: "Please provide your information to book the appointment",
//     nameLabel: "Full Name",
//     namePlaceholder: "Enter your full name",
//     emailLabel: "Email Address",
//     emailPlaceholder: "Enter your email",
//     phoneLabel: "Phone Number",
//     phonePlaceholder: "Enter your phone number",
//     backBtn: "Back",
//     continueBtn: "Continue",
//   },
//   hi: {
//     title: "रोगी विवरण",
//     subtitle: "अपॉइंटमेंट बुक करने के लिए अपनी जानकारी प्रदान करें",
//     nameLabel: "पूरा नाम",
//     namePlaceholder: "अपना पूरा नाम दर्ज करें",
//     emailLabel: "ईमेल पता",
//     emailPlaceholder: "अपना ईमेल दर्ज करें",
//     phoneLabel: "फ़ोन नंबर",
//     phonePlaceholder: "अपना फ़ोन नंबर दर्ज करें",
//     backBtn: "वापस",
//     continueBtn: "जारी रखें",
//   },
//   te: {
//     title: "రోగి వివరాలు",
//     subtitle: "అపాయింట్మెంట్ బుక్ చేయడానికి మీ సమాచారం అందించండి",
//     nameLabel: "పూర్తి పేరు",
//     namePlaceholder: "మీ పూర్తి పేరు నమోదు చేయండి",
//     emailLabel: "ఇమెయిల్ చిరునామా",
//     emailPlaceholder: "మీ ఇమెయిల్ నమోదు చేయండి",
//     phoneLabel: "ఫోన్ నంబర్",
//     phonePlaceholder: "మీ ఫోన్ నంబర్ నమోదు చేయండి",
//     backBtn: "వెనుకకు",
//     continueBtn: "కొనసాగించు",
//   },
// };

// export default function PatientDetailsScreen({
//   lang = "en",
//   onContinue,
//   onBack,
// }) {
//   const c = COPY[lang] || COPY.en;
//   const [name, setName] = useState("");
//   const [email, setEmail] = useState("");
//   const [phone, setPhone] = useState("");
//   const [errors, setErrors] = useState({
//     name: "",
//     email: "",
//     phone: "",
//   });
//   const canContinue = name.trim() && email.trim() && phone.trim();
//   const validate = () => {
//     const newErrors = {
//       name: "",
//       email: "",
//       phone: "",
//     };

//     if (!name.trim()) {
//       newErrors.name = "Name is required";
//     }

//     if (!email.trim()) {
//       newErrors.email = "Email is required";
//     } else if (!/^\S+@\S+\.\S+$/.test(email)) {
//       newErrors.email = "Enter a valid email";
//     }

//     if (!phone.trim()) {
//       newErrors.phone = "Phone is required";
//     } else if (!/^\d{10}$/.test(phone)) {
//       newErrors.phone = "Phone must be 10 digits";
//     }

//     setErrors(newErrors);

//     return !newErrors.name && !newErrors.email && !newErrors.phone;
//   };

//   return (
//     <div className={styles.bookingFormScreen}>
//       <button className={styles.selectDoctorBack} onClick={onBack}>
//         ← Back
//       </button>
//       {/* Icon */}
//       <div className={styles.bookingIconCircle}>
//         <PersonIcon size={30} />
//       </div>
//       <div className={styles.bookingFormTitle}>{c.title}</div>
//       <div className={styles.bookingFormSub}>{c.subtitle}</div>

//       {/* Full Name */}
//       <div className={styles.bookingFieldGroup}>
//         <label className={styles.bookingFieldLabel}>{c.nameLabel}</label>
//         <div className={styles.bookingInputWrap}>
//           <span className={styles.bookingInputIcon}>
//             <UserIcon />
//           </span>
//           <input
//             className={styles.bookingInput}
//             placeholder={c.namePlaceholder}
//             value={name}
//             onChange={(e) => setName(e.target.value)}
//           />
//         </div>
//       </div>

//       {/* Email */}
//       <div className={styles.bookingFieldGroup}>
//         <label className={styles.bookingFieldLabel}>{c.emailLabel}</label>
//         <div className={styles.bookingInputWrap}>
//           <span className={styles.bookingInputIcon}>
//             <MailIcon />
//           </span>
//           <input
//             className={styles.bookingInput}
//             placeholder={c.emailPlaceholder}
//             type="email"
//             value={email}
//             onChange={(e) => setEmail(e.target.value)}
//           />
//         </div>
//         {errors.email && <p className={styles.errorText}>{errors.email}</p>}
//       </div>

//       {/* Phone */}
//       <div className={styles.bookingFieldGroup}>
//         <label className={styles.bookingFieldLabel}>{c.phoneLabel}</label>
//         <div className={styles.bookingInputWrap}>
//           <span className={styles.bookingInputIcon}>
//             <PhoneIcon />
//           </span>
//           <span
//             className={`${styles.bookingInputDigit}`}
//             style={{ color: "#374151" }}
//           >
//             +91
//           </span>
//           <input
//             // className={styles.bookingInput}
//             className="px-2 py-3 text-sm outline-none bg-white"
//             placeholder={c.phonePlaceholder}
//             // type="number"
//             value={phone}
//             type="tel"
//             maxLength={10}
//             onChange={(e) => {
//               const value = e.target.value.replace(/\D/g, "");
//               setPhone(value);
//             }}
//             // onChange={(e) => setPhone(e.target.value)}
//           />
//         </div>
//         {errors.phone && <p className={styles.errorText}>{errors.phone}</p>}
//       </div>

//       <div className={styles.bookingActions}>
//         {/* <button className={styles.bookingBackBtn} onClick={onBack}>
//           {c.backBtn}
//         </button> */}
//         <button
//           className={styles.bookingContinueBtn}
//           disabled={!canContinue}
//           // onClick={() => onContinue({ name, email, phone })}
//           onClick={() => {
//             const ok = validate();
//             if (ok) onContinue({ name, email, phone });
//           }}
//         >
//           {c.continueBtn}
//         </button>
//       </div>
//     </div>
//   );
// }
